/*
 * The card both first-run screens are drawn in (VISUAL.md §7.16): the rocket, the greeting, whatever
 * the screen's own body is, and one foot with *Got it*.
 *
 * The two screens differ in their body — the member gets three doors, the administrator the setup
 * checklist — and in nothing else, so the frame is written once: the same class pair, the same title
 * relation (`aria-labelledby`, because the card is a `section` and its heading is the screen's `h1`),
 * the same picture at its two sizes, and the same dismissal. `compact` is the block above the channel
 * list on a phone (§7.19): no picture over the heading — one that tall would push the list it
 * introduces off the screen — a small mark beside it instead, and no keyboard hint, because a phone
 * has no keyboard.
 */

import type { ReactNode } from "react"
import { Button } from "../../ui/index.ts"
import { t } from "../i18n/index.ts"
import "./home.css"

/** The rocket with its earth, used as a greeting on these two screens, on Home and on the 404. */
export const ROCKET_IN_SPACE = "/icons/rocket-in-space.svg"

/** The heading's id: one screen, one `h1`, named by the card around it. */
const TITLE_ID = "tb-firstrun-title"

export function FirstRunCard({
	title,
	lead,
	compact,
	onDismiss,
	hint,
	children,
}: {
	/** Already translated. */
	title: string
	/** Already translated. */
	lead: string
	/** The block above the channel list on a narrow window. */
	compact: boolean
	onDismiss: () => void
	/** One quiet line above *Got it*, on a window that has a keyboard to name keys for. */
	hint?: string | undefined
	children: ReactNode
}) {
	const rocket = <img className="tb-firstrun__rocket" src={ROCKET_IN_SPACE} alt="" />
	return (
		<section
			className={compact ? "tb-firstrun tb-firstrun--compact" : "tb-page tb-firstrun"}
			aria-labelledby={TITLE_ID}
		>
			{compact ? null : rocket}
			<div className="tb-firstrun__head">
				{compact ? rocket : null}
				<div className="tb-page__head">
					<h1 id={TITLE_ID}>{title}</h1>
					<p className="tb-page__lead">{lead}</p>
				</div>
			</div>
			{children}
			<div className="tb-firstrun__foot">
				{hint === undefined || compact ? null : <p className="tb-firstrun__hint">{hint}</p>}
				<Button variant="ghost" label={t("shell.firstRun.dismiss")} onClick={onDismiss} />
			</div>
		</section>
	)
}
